import React, { FC, useRef } from 'react';

import {
  Box,
  Menu,
  Flex,
  Text,
  Stack,
  Center,
  Button,
  Heading,
  MenuItem,
  MenuList,
  MenuButton,
} from '@chakra-ui/react';
import { HiDocumentAdd } from 'react-icons/hi';
import { MdArrowDropDown, MdUploadFile } from 'react-icons/md';

import { washHub } from 'store/slices';

import { useMutation } from 'react-query';
import { exportCsvCases } from 'api/items';

import { CSVLink } from 'react-csv';
import { UserPill } from 'components/core/UserPill';
import { Filters } from 'components/primitives/Filters';
import { StatCards } from 'components/primitives/StatCards';
import { DataTable } from 'components/modules/DataTable';
import { AddCaseModal } from 'components/modules/AddCaseModal';
import { EditCaseModal } from 'components/modules/EditCaseModal';
import { UploadCSVModal } from 'components/modules/UploadCSVModal';
import { AddManualCaseModal } from 'components/modules/AddManualCaseModal';

import { Case } from 'types/items';
import { CasesViewProps } from './Cases.props';
import { casesColumns } from './casesColumns';

const CasesView: FC<CasesViewProps> = (props) => {
  const {
    sku,
    embedded,
    cases,
    venue,
    status,
    setPage,
    setStatus,
    onDelete,
    isLoading,
    isDeleting,
    pagination,
    onUpdateClick,
    onApplyFilters,
    onClearFilters,
  } = props;

  const csvRef = useRef<any>(null);
  const addCaseModalRef = useRef<any>(null);
  const editCaseModalRef = useRef<any>(null);
  const uploadCSVModalRef = useRef<any>(null);
  const addManualCaseModalRef = useRef<any>(null);

  const selectedWashHub = washHub.selectors.useSelectedWashHub();
  const washHubId = selectedWashHub?._id || '';
  const {
    mutate: mutateCsv,
    data: csvData = '',
    isLoading: isLoadingCSV,
  } = useMutation(
    async () => {
      const response = await exportCsvCases({
        sku,
        venue,
        washHub: washHubId,
        status,
      });

      return response;
    },
    {
      onSuccess: () => {
        setTimeout(() => {
          csvRef.current?.link?.click?.();
        }, 500);
      },
    },
  );

  const renderExpBtn = () => {
    return (
      <>
        <Button
          isLoading={isLoadingCSV}
          ml={2}
          minW="200px"
          onClick={() => {
            mutateCsv();
          }}>
          Export Cases to CSV
        </Button>
        {!isLoadingCSV && Boolean(csvData) && (
          <CSVLink
            data={csvData}
            ref={csvRef}
            filename={`washhub-${selectedWashHub?.name}-cases.csv`}
            target="_blank"
          />
        )}
      </>
    );
  };

  return (
    <Flex
      direction="column"
      flex={1}
      color="support.500"
      overflow={embedded ? undefined : 'auto'}>
      <Flex alignItems="baseline" justify="space-between">
        {embedded ? <Box /> : <Heading fontSize="24px">Cases</Heading>}
        <Stack isInline>
          {renderExpBtn()}
          <Button
            w="200px"
            leftIcon={<MdUploadFile size="20px" />}
            onClick={() => {
              uploadCSVModalRef?.current?.onOpen?.();
            }}>
            Upload CSV
          </Button>

          <Menu>
            <MenuButton
              isDisabled={washHubId === ''}
              as={Button}
              w="200px"
              leftIcon={<HiDocumentAdd size="20px" />}
              rightIcon={<MdArrowDropDown size="20px" />}>
              Create Case
            </MenuButton>
            <MenuList>
              <MenuItem
                onClick={() => {
                  addCaseModalRef?.current?.onOpen?.();
                }}>
                EPC
              </MenuItem>
              <MenuItem
                onClick={() => {
                  addManualCaseModalRef?.current?.onOpen?.();
                }}>
                Non EPC
              </MenuItem>
            </MenuList>
          </Menu>

          {!embedded && <UserPill />}
        </Stack>
      </Flex>
      <AddCaseModal ref={addCaseModalRef} />
      <EditCaseModal ref={editCaseModalRef} />
      <AddManualCaseModal ref={addManualCaseModalRef} />
      <UploadCSVModal type={'case'} ref={uploadCSVModalRef} />
      <Flex h={embedded ? '20px' : '50px'} />
      <StatCards
        type={`cases`}
        selected={status}
        onSelectStatus={(_selected) => {
          if (Array.isArray(_selected)) {
            setPage(1);
            setStatus(_selected);
          }
        }}
      />
      <Flex direction="column" position="relative">
        <Flex>
          <Filters
            venue={venue}
            isFullButton
            type="case"
            onApplyFilters={onApplyFilters}
            onClearFilters={onClearFilters}
          />
          <Flex h="10px" w="20px" />
          <Button
            w="180px"
            borderRadius="5px"
            onClick={onUpdateClick}
            isLoading={isLoading}>
            Update Table
          </Button>
        </Flex>
        <DataTable<Case>
          mt={0}
          minW="2000px"
          data={cases}
          columns={casesColumns}
          isLoading={isLoading}
          pagination={pagination}
          onRowClick={(_case) => {
            editCaseModalRef?.current?.onOpen?.(_case);
          }}
          renderEmpty={() => (
            <Center p={8}>
              <Text fontSize="20px">No Cases available</Text>
            </Center>
          )}
        />
      </Flex>
    </Flex>
  );
};

export default CasesView;
